<template>
  <q-card
    :bordered="!printingMode && !transparentCard"
    :class="{ 'bg-transparent': transparentCard }"
    flat
  >
    <div
      v-if="!hideHeader && !isLoading && !hasError"
      :class="[
        printingMode ? 'q-pb-md' : 'q-pa-md',
        $q.screen.lt.sm ? 'column items-start' : 'row justify-between items-center'
      ]"
    >
      <div class="col" style="min-width: 35%">
        <div :class="printingMode ? 'text-h4 text-primary text-bold' : 'text-h6'">
          {{ title }}
        </div>
        <div :class="printingMode ? 'text-grey-8' : ''" class="description-text">
          {{ description }}
        </div>
      </div>
      <div
        v-if="!printingMode"
        ref="actionsContainer"
        data-html2canvas-ignore
        :class="[actionsContainerClass, $q.screen.lt.sm ? 'q-mt-xs' : '']"
      >
        <slot name="actions"> </slot>
      </div>
    </div>
    <template v-if="isLoading">
      <!-- Header Skeleton -->
      <div v-if="!hideHeader" id="intercom-header" class="q-pa-md flex justify-between">
        <div>
          <q-skeleton style="width: 150px; height: 32px" />
          <q-skeleton style="width: 300px" type="text" />
        </div>
        <div class="flex q-gutter-sm">
          <q-skeleton style="width: 150px; height: 40px" />
        </div>
      </div>
      <slot name="loading"> </slot>
    </template>
    <template v-else-if="hasError">
      <div v-if="!hideHeader" class="q-pa-md">
        <div :class="printingMode ? 'text-h4 text-primary text-bold' : 'text-h6'">
          {{ title }}
        </div>
        <div :class="printingMode ? 'text-grey-8' : ''" class="description-text">
          {{ description }}
        </div>
      </div>
      <q-separator />
      <div class="column justify-center items-center q-mt-md text-grey-7">
        <q-icon name="close" size="xl" />
        <div class="text-body2 text-grey-6 q-mt-md">
          {{ errorMessageText }}
        </div>
      </div>
    </template>
    <template v-else>
      <div v-if="isEmpty">
        <q-separator />
        <no-units-found />
      </div>
      <template v-else>
        <slot name="default"> </slot>
      </template>
    </template>
  </q-card>
</template>

<script lang="ts">
import NoUnitsFound from '@/components/NoUnitsFound.vue'

export default {
  name: 'PropertyReportCard',
  components: { NoUnitsFound },
  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    printingMode: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    hideHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    transparentCard: {
      type: Boolean,
      required: false,
      default: false
    },
    isEmpty: {
      type: Boolean,
      required: false,
      default: false
    },
    hasError: {
      type: Boolean,
      required: false,
      default: false
    },
    errorMessage: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      numActions: 0
    }
  },
  computed: {
    actionsContainerClass() {
      if (this.numActions >= 4 && this.$q.screen.lt.lg) {
        return 'actions-grid items-center q-gutter-xs justify-end'
      }
      if (this.numActions === 0) {
        return ''
      }
      return 'row items-center q-gutter-md justify-end ' + this.getColSizeByScreenSize()
    },
    errorMessageText() {
      return (
        this.errorMessage ||
        'Encountered an error while retrieving market data. Please try again later.'
      )
    }
  },
  mounted() {
    this.updateNumActions()
  },
  methods: {
    updateNumActions(): void {
      this.$nextTick(() => {
        const actionsContainer = this.$refs.actionsContainer as HTMLElement
        if (actionsContainer) {
          // Count direct children of the slot
          const slotChildren = actionsContainer.children
          this.numActions = slotChildren.length
        }
      })
    },
    getColSizeByScreenSize(): string {
      if (this.$q.screen.gt.xs) {
        return 'max-content'
      }
      return 'col'
    }
  }
}
</script>

<style scoped>
.description-text {
  font-size: 12px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

/* md screen size */
@media (min-width: 1024px) {
  .actions-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 24px;
}

.max-content {
  width: max-content;
}

@media print {
  .description-text {
    font-size: 16px;
  }
}
</style>
